<template>
  <div>
    <v-row dense class="pb-2 align-center">
      <v-col
        cols="12"
        md="6"
        offset-md="6"
        offset-lg="8"
        lg="4"
        class="d-flex text-right align-center"
      >
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          class="mr-md-2"
          solo
          hide-details
          dense
          clearable
          placeholder="Search Architects"
        ></v-text-field>
        <v-btn icon small class="ml-2" @click="refresh">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-card v-if="!architects || architects.length < 1">
      <v-card-text>
        <div class="text-center">
          No Architect Company found...
        </div>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="architects"
      :search="searchQuery"
      no-data-text="No architect company found"
    >
      <template v-slot:[`item.name`]="{ item }">
        <div class="d-flex align-center pr-2 py-2">
          <div>
            <v-avatar v-if="!item.logo_url" tile color="primary" size="60">
              <span class="display-1 text-capitalize white--text">{{
                item.name.charAt(0)
              }}</span>
            </v-avatar>
            <v-avatar v-else tile size="60">
              <v-img :src="item.logo_url" :alt="item.name" contain />
            </v-avatar>
          </div>
          <div class="ml-1">
            <h6 class="primary--text pb-0 mb-0">
              <strong>{{ item.name }}</strong>
            </h6>
            <div>
              <span v-if="item.address">
                {{ item.address.city && `${item.address.city}` }}
                {{ item.address.state && `, ${item.address.state}` }}
              </span>
            </div>
            <div>
              <span>{{ item.address.phone || "N/A" }} </span>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:[`item.is_suspended`]="{ item }">
        <span :class="item.is_suspended ? 'red--text' : 'success--text'">{{
          item.is_suspended ? "Suspended" : "Active"
        }}</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-menu offset-y left>
          <template v-slot:activator="{ on }">
            <transition name="slide-fade" mode="out-in">
              <v-btn icon v-on="on">
                <v-icon size="32" color="primary">
                  mdi-dots-horizontal
                </v-icon>
              </v-btn>
            </transition>
          </template>
          <v-list dense>
            <v-list-item @click="updateUser(item)">
              <v-icon size="18" class="mr-1">mdi-cog</v-icon>
              <v-list-item-title>Edit</v-list-item-title>
            </v-list-item>
            <v-list-item @click="manageUsers(item)">
              <v-icon size="18" class="mr-1">mdi-account-multiple</v-icon>
              <v-list-item-title>Manage Users</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="!item.is_suspended"
              @click="deactivateDealer(item)"
            >
              <v-icon size="18" class="mr-1">mdi-minus-circle-outline</v-icon>
              <v-list-item-title>Suspend</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="item.is_suspended"
              @click="reactivateDealer(item)"
            >
              <v-icon size="18" class="mr-1">mdi-plus-circle-outline</v-icon>
              <v-list-item-title>Unsuspend</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteDealer(item)">
              <v-icon size="18" class="mr-1">mdi-delete</v-icon>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <AddEditArchitect
      v-if="editModal"
      :dialog="editModal"
      :architectInfo="selectedItem"
      :edit="true"
      @refresh="refresh"
      @close="editModal = false"
    />

    <DeactivateArchitect
      v-if="deactivateModal && selectedItem"
      :dialog="deactivateModal"
      :architectInfo="selectedItem"
      @refresh="refresh"
      @close="deactivateModal = false"
    />

    <ReactivateArchitect
      v-if="reactivateModal && selectedItem"
      :dialog="reactivateModal"
      :architectInfo="selectedItem"
      @refresh="refresh"
      @close="reactivateModal = false"
    />

    <DeleteArchitect
      v-if="deleteModal && selectedItem"
      :dialog="deleteModal"
      :item="selectedItem"
      @refresh="refresh"
      @close="deleteModal = false"
    />

    <UsersModal
      v-if="usersModal && selectedItem"
      :dialog="usersModal"
      :company="selectedItem"
      @refresh="refresh"
      @close="usersModal = false"
    />
  </div>
</template>

<script>
import AddEditArchitect from "@/components/architect/AddEditArchitect";
import DeactivateArchitect from "@/components/architect/DeactivateArchitect";
import ReactivateArchitect from "@/components/architect/ReactivateArchitect";
import DeleteArchitect from "@/components/architect/DeleteArchitect";
import UsersModal from "@/components/architect/UsersModal";
export default {
  components: {
    AddEditArchitect,
    DeactivateArchitect,
    ReactivateArchitect,
    DeleteArchitect,
    UsersModal
  },
  props: {
    companyList: { type: Array, default: null },
    paginationObj: { type: Object, default: null },
    isLoading: { type: Boolean, default: false }
  },
  data() {
    return {
      architects: [],
      selectedItem: null,
      pagination: null,
      editModal: false,
      usersModal: false,
      deleteModal: false,
      deactivateModal: false,
      reactivateModal: false,
      searchQuery: null,
      headers: [
        {
          text: "architects",
          value: "name"
        },
        {
          text: "Status",
          value: "is_suspended"
        },
        {
          text: "Actions",
          value: "actions"
        }
      ]
    };
  },
  watch: {
    companyList(val) {
      if (val && val.length) {
        this.architects = JSON.parse(JSON.stringify(val));
      }
    }
  },
  created() {
    if (this.companyList && this.companyList.length) {
      this.architects = JSON.parse(JSON.stringify(this.companyList));
    }
  },
  methods: {
    deleteDealer(item) {
      this.selectedItem = { ...item };
      this.deleteModal = true;
    },
    updateUser(item) {
      this.selectedItem = { ...item };
      this.editModal = true;
    },
    manageUsers(item) {
      this.selectedItem = { ...item };
      this.usersModal = true;
    },
    deactivateDealer(item) {
      this.selectedItem = { ...item };
      this.deactivateModal = true;
    },
    reactivateDealer(item) {
      this.selectedItem = { ...item };
      this.reactivateModal = true;
    },
    refresh() {
      this.deleteModal = false;
      this.editModal = false;
      this.inviteModal = false;
      this.usersModal = false;
      this.deactivateModal = false;
      this.reactivateModal = false;

      this.$emit("refresh", true);
    }
  }
};
</script>
